import styles from './TasksList.module.scss';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ContextMenu, DateValue, TableColumnType } from '@/components';
import { NavLink } from 'react-router-dom';
import {
  formatters,
  TaskStatus,
  CommunityPermissions,
  StringFilterOptions,
} from '@/core';
import { TaskState } from '@/redux';
import { sort, useFiltersFactory } from '@/utils';
import { Assert } from '../Communities.Common';
import { TaskStatusForm, useProjectContext } from '..';
import { useDeleteTask } from '../Tasks.Details/useDeleteTask';
import { Button } from 'antd';

const isGroupRow = (item: TaskState) => Array.isArray((item as any).children);

export function useTasksColumns(
  openEdit: (id: string) => any,
  openDuplicate: (taskIds: string[], projectId: string) => any,
  openEditComments: (id: string) => any,
  handleUngroup: (id: string) => any,
  openEditGroup: (id: string) => any,
  tasks?: TaskState[],
  tasksStringFilterOptions?: StringFilterOptions,
) {
  const { t } = useTranslation();
  const { isActive: isActiveProject } = useProjectContext();
  const filters = useFiltersFactory(tasks, tasksStringFilterOptions);

  const handleDelete = useDeleteTask(false);

  return useMemo<TableColumnType<TaskState>[]>(() => {
    const columns: Array<TableColumnType<TaskState> | false> = [
      {
        key: 'expanded',
        width: '50px',
      },
      {
        title: t('tasks.groups.name'),
        key: 'groupName',
        width: '10%',
        render: (_, item: any) =>
          isGroupRow(item) ? <strong>{item.groupName}</strong> : null,
      },
      {
        title: t('tasks.number'),
        key: 'number',
        width: '7%',
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.number.localeCompare(b.number),
        render: (_, item) => (isGroupRow(item) ? null : item.number),
        ...filters.select((x) => x.number, 'number'),
      },
      {
        title: t('tasks.result'),
        key: 'status',
        width: '15%',
        sorter: (a, b) => a.status.localeCompare(b.status),
        ...filters.enumSelect((x) => x.status, TaskStatus),
        onCell: () => ({
          className: 'pb-0',
        }),
        render: (_, item) =>
          isGroupRow(item) ? null : (
            <TaskStatusForm
              key={`${item.id}-${item.status}`}
              task={item}
              className="w-100"
            />
          ),
      },
      {
        title: t('tasks.details.comments'),
        key: 'comments',
        width: '10%',
        render: (_, item) =>
          isGroupRow(item) ? null : (
            <Button
              className={styles.commentsBtn}
              type="link"
              onClick={() => openEditComments(item.id)}
            >
              {item.hasComments
                ? t('tasks.comments.view')
                : t('tasks.comments.add')}
            </Button>
          ),
      },
      {
        title: t('tasks.name'),
        key: 'name',
        width: '15%',
        sorter: (a, b) => a.name.localeCompare(b.name),
        ...filters.select((x) => x.name, 'name'),
        render: (_, item) =>
          isGroupRow(item) ? null : (
            <NavLink
              to={`/projects/${item.projectId}/task-management/${item.id}`}
            >
              {item.name}
            </NavLink>
          ),
      },
      {
        title: t('tasks.home'),
        key: 'home',
        width: '10%',
        sorter: (a, b) => a.homeName.localeCompare(b.homeName),
        ...filters.select((x) => x.homeName, 'home'),
        render: (_, item) => (isGroupRow(item) ? null : item.homeName),
      },
      {
        title: t('tasks.step'),
        key: 'stepName',
        width: '7%',
        sorter: (a, b) => sort(a.stepName, b.stepName),
        ...filters.select((x) => x.stepName, 'stepName'),
        render: (_, item) => (isGroupRow(item) ? null : item.stepName),
      },
      {
        title: t('tasks.startDate'),
        key: 'startDate',
        width: '10%',
        sorter: (a, b) => getTime(a.startDate) - getTime(b.startDate),
        ...filters.dateTimeRange((x) => x.startDate),
        render: (_, item) =>
          isGroupRow(item) ? null : (
            <DateValue value={item.startDate} applyOffset={false} />
          ),
      },
      {
        title: t('tasks.endDate'),
        key: 'endDate',
        width: '10%',
        sorter: (a, b) => getTime(a.endDate) - getTime(b.endDate),
        ...filters.dateTimeRange((x) => x.endDate),
        render: (_, item) =>
          isGroupRow(item) ? null : (
            <DateValue value={item.endDate} applyOffset={false} />
          ),
      },
      {
        title: t('tasks.responsiblePerson'),
        key: 'responsiblePerson',
        width: '15%',
        sorter: (a, b) =>
          sort(a.responsiblePerson?.label, b.responsiblePerson?.label),
        ...filters.select(
          (x) => x.responsiblePerson?.label,
          'responsiblePerson',
        ),
        render: (_, item) =>
          isGroupRow(item) ? null : item.responsiblePerson?.label,
      },
      {
        title: t('tasks.costEstimated'),
        key: 'costEstimated',
        width: '15%',
        sorter: (a, b) => a.estimatedCost - b.estimatedCost,
        ...filters.numberRange((x) => x.estimatedCost),
        render: (_, item) =>
          isGroupRow(item) ? null : formatters.price(item.estimatedCost),
        className: 'text-end',
      },
      {
        title: t('tasks.completedDate'),
        key: 'completedDate',
        width: '10%',
        sorter: (a, b) => getTime(a.completedDate) - getTime(b.completedDate),
        ...filters.dateTimeRange((x) => x.completedDate),
        render: (_, item) =>
          isGroupRow(item)
            ? null
            : item.completedDate
            ? formatters.dateTime(item.completedDate, 'date')
            : null,
      },
      {
        title: t('tasks.completedBy'),
        key: 'completedBy',
        width: '10%',
        render: (_, item) => (isGroupRow(item) ? null : item.completedByName),
      },
      {
        title: t('tasks.actions'),
        key: 'actions',
        width: '7%',
        align: 'center',
        render: (_, item) =>
          isGroupRow(item) ? (
            <Assert
              permission={CommunityPermissions.DevelopScope.Manage}
              active
            >
              <ContextMenu
                items={[
                  {
                    label: t('tasks.contextMenu.editGroupButton'),
                    onClick: () =>
                      openEditGroup(item.id.substring('group-'.length)),
                  },
                  {
                    label: t('tasks.contextMenu.ungroupButton'),
                    onClick: () =>
                      handleUngroup(item.id.substring('group-'.length)),
                  },
                ]}
              />
            </Assert>
          ) : (
            isActiveProject && (
              <Assert
                permission={CommunityPermissions.DevelopScope.Manage}
                active
              >
                <ContextMenu
                  items={[
                    {
                      label: t('tasks.contextMenu.editButton'),
                      onClick: () => openEdit(item.id),
                    },
                    {
                      label: t('tasks.contextMenu.deleteButton'),
                      onClick: () => handleDelete(item.id, undefined),
                    },
                    {
                      label: t('tasks.contextMenu.duplicateButton'),
                      onClick: () => openDuplicate([item.id], item.projectId),
                    },
                  ]}
                />
              </Assert>
            )
          ),
      },
    ];
    return columns.filter((x) => !!x) as TableColumnType<TaskState>[];
  }, [
    t,
    filters,
    openEditComments,
    isActiveProject,
    openEditGroup,
    handleUngroup,
    openEdit,
    handleDelete,
    openDuplicate,
  ]);
}

export function getTime(text?: string) {
  return text ? new Date(text).getTime() : 0;
}
